import { useEffect, useState } from "react";
import { Plus, RefreshCw, Download, Scissors, BedDouble, Store, Users as UsersIcon, Landmark, BellRing, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const ROLES = [["admin", "Admin"], ["manager", "Manager"], ["cashier", "Cashier"], ["groomer", "Groomer"], ["kennel_staff", "Kennel Staff"]];
const EMPTY_USER = { name: "", email: "", password: "", role: "cashier", commission_rate: 0, phone: "" };
const EMPTY_SVC = { name: "", tier: "Standard", base_price: "", duration_min: 60, description: "" };
const EMPTY_ROOM = { name: "", type: "kennel", daily_rate: "", capacity: 1 };

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [journals, setJournals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [services, setServices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [userDialog, setUserDialog] = useState(false);
  const [userForm, setUserForm] = useState(EMPTY_USER);
  const [editUser, setEditUser] = useState(null);
  const [svcDialog, setSvcDialog] = useState(false);
  const [svcForm, setSvcForm] = useState(EMPTY_SVC);
  const [roomDialog, setRoomDialog] = useState(false);
  const [roomForm, setRoomForm] = useState(EMPTY_ROOM);
  const [svcEdit, setSvcEdit] = useState(null);
  const [roomEdit, setRoomEdit] = useState(null);

  const load = () => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/accounting/journals").then((r) => setJournals(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [user]);

  const saveSettings = async () => {
    try { await api.put("/settings", settings); toast.success("Store settings saved"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const saveUser = async () => {
    try {
      await api.post("/users", { ...userForm, commission_rate: parseFloat(userForm.commission_rate) || 0 });
      toast.success("Staff account created");
      setUserDialog(false); setUserForm(EMPTY_USER); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Account deactivated" : "Account activated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const saveEditUser = async () => {
    try {
      const payload = { name: editUser.name, role: editUser.role, phone: editUser.phone || "" };
      if (editUser.role === "groomer") payload.commission_rate = parseFloat(editUser.commission_rate) || 0;
      if (editUser.password) payload.password = editUser.password;
      await api.put(`/users/${editUser.id}`, payload);
      toast.success("Staff account updated");
      setEditUser(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.role.replace("_", " ")})? Their past sales/services stay on record, but they will lose access.`)) return;
    try {
      const { data } = await api.delete(`/users/${u.id}`);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveService = async () => {
    try {
      await api.post("/services", { ...svcForm, base_price: parseFloat(svcForm.base_price) || 0, duration_min: parseInt(svcForm.duration_min) || 60 });
      toast.success("Service added"); setSvcDialog(false); setSvcForm(EMPTY_SVC); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteService = async (id) => {
    try { await api.delete(`/services/${id}`); toast.success("Service removed"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const saveSvcEdit = async () => {
    try {
      await api.put(`/services/${svcEdit.id}`, { ...svcEdit, base_price: parseFloat(svcEdit.base_price) || 0, duration_min: parseInt(svcEdit.duration_min) || 60 });
      toast.success("Service updated");
      setSvcEdit(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleSvcActive = async (s) => {
    try {
      await api.put(`/services/${s.id}`, { ...s, active: !s.active });
      toast.success(s.active ? "Service hidden from online booking" : "Service visible for booking");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveRoomEdit = async () => {
    try {
      await api.put(`/rooms/${roomEdit.id}`, { ...roomEdit, daily_rate: parseFloat(roomEdit.daily_rate) || 0, capacity: parseInt(roomEdit.capacity) || 1 });
      toast.success("Room updated");
      setRoomEdit(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleRoomActive = async (r) => {
    try {
      await api.put(`/rooms/${r.id}`, { ...r, active: !r.active });
      toast.success(r.active ? "Room deactivated — hidden from booking" : "Room activated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteRoom = async (r) => {
    if (!window.confirm(`Remove room "${r.name}"? Rooms with upcoming reservations cannot be removed — deactivate instead.`)) return;
    try {
      await api.delete(`/rooms/${r.id}`);
      toast.success("Room removed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveRoom = async () => {
    try {
      await api.post("/rooms", { ...roomForm, daily_rate: parseFloat(roomForm.daily_rate) || 0, capacity: parseInt(roomForm.capacity) || 1 });
      toast.success("Room added"); setRoomDialog(false); setRoomForm(EMPTY_ROOM); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const syncAccounting = async () => {
    try {
      const { data } = await api.post("/accounting/sync");
      toast.success(data.message);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/accounting/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "journal_entries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="animate-rise space-y-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="settings-title">Settings &amp; Administration</h1>
        <p className="text-sm text-slate-500">Store profile, staff accounts, catalogs, accounting and notification logs.</p>
      </div>

      <Tabs defaultValue="store">
        <TabsList className="bg-[#E2F5F5] flex-wrap h-auto">
          <TabsTrigger value="store" data-testid="set-tab-store"><Store className="w-3.5 h-3.5 mr-1" /> Store</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="set-tab-users"><UsersIcon className="w-3.5 h-3.5 mr-1" /> Staff &amp; Roles</TabsTrigger>}
          <TabsTrigger value="catalog" data-testid="set-tab-catalog"><Scissors className="w-3.5 h-3.5 mr-1" /> Services &amp; Rooms</TabsTrigger>
          <TabsTrigger value="accounting" data-testid="set-tab-accounting"><Landmark className="w-3.5 h-3.5 mr-1" /> Accounting</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="set-tab-notifications"><BellRing className="w-3.5 h-3.5 mr-1" /> Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="store" className="mt-4">
          {settings && (
            <div className="bg-white rounded-2xl border border-[#BFE8E9] p-6 max-w-2xl space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Store name</Label><Input value={settings.store_name} onChange={(e) => setSettings({ ...settings, store_name: e.target.value })} data-testid="set-store-name" /></div>
                <div><Label>TIN</Label><Input value={settings.tin} onChange={(e) => setSettings({ ...settings, tin: e.target.value })} data-testid="set-tin" /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} data-testid="set-address" /></div>
                <div><Label>Phone</Label><Input value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} data-testid="set-phone" /></div>
                <div><Label>BIR Permit</Label><Input value={settings.bir_accred || ""} onChange={(e) => setSettings({ ...settings, bir_accred: e.target.value })} data-testid="set-bir" /></div>
                <div className="col-span-2"><Label>Notification email (order &amp; booking alerts)</Label><Input value={settings.notify_email || ""} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} placeholder="Defaults to the admin email" data-testid="set-notify-email" /></div>
              </div>
              <p className="text-xs text-slate-500">VAT rate is fixed at 12% per PH law. Receipts print with TIN and BIR permit details.</p>
              <Button className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={saveSettings} data-testid="set-save-btn">Save settings</Button>
            </div>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="mt-4 space-y-4">
            <div className="flex justify-end">
              <Button className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => setUserDialog(true)} data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" /> Add Staff</Button>
            </div>
            <div className="bg-white rounded-2xl border border-[#BFE8E9] overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Commission</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {users.filter((u) => u.role !== "customer").map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                      <TableCell><Badge variant="secondary" className="capitalize text-[10px]">{u.role.replace("_", " ")}</Badge></TableCell>
                      <TableCell className="text-right">{u.role === "groomer" ? `${Math.round((u.commission_rate || 0) * 100)}%` : "—"}</TableCell>
                      <TableCell>{u.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge> : <Badge className="bg-red-100 text-red-600 hover:bg-red-100">Inactive</Badge>}</TableCell>
                      <TableCell>
                        {u.id !== user.id && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-7" title="Edit name, role, commission"
                              onClick={() => setEditUser({ ...u, password: "" })} data-testid={`edit-user-${u.email}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.email}`}>
                              {u.active ? "Deactivate" : "Activate"}
                            </Button>
                            {u.role !== "admin" && (
                              <Button variant="ghost" size="sm" className="h-7 text-red-500" title="Remove staff"
                                onClick={() => deleteUser(u)} data-testid={`delete-user-${u.email}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}

        <TabsContent value="catalog" className="mt-4 grid lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Grooming services</h3>
              <Button variant="outline" size="sm" onClick={() => setSvcDialog(true)} data-testid="add-service-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {services.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 text-sm border-b border-[#E2F5F5] pb-2" data-testid={`svc-row-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="min-w-0">
                    <div className="font-medium">
                      {s.name} <Badge variant="outline" className="text-[9px] ml-1">{s.tier}</Badge>
                      {!s.active && <Badge className="ml-1 bg-slate-100 text-slate-500 hover:bg-slate-100 text-[9px]">hidden</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">from {peso(s.base_price)} · {s.duration_min} min · weight-tiered pricing</p>
                    {s.description && <p className="text-xs text-slate-400 mt-0.5 truncate">{s.description}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-7" title="Edit price, duration, description" onClick={() => setSvcEdit({ ...s })} data-testid={`edit-svc-${s.id.slice(0, 6)}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => toggleSvcActive(s)} data-testid={`toggle-svc-${s.id.slice(0, 6)}`}>{s.active ? "Hide" : "Show"}</Button>
                    <Button variant="ghost" size="sm" className="text-red-500 h-7" title="Remove service" onClick={() => deleteService(s.id)} data-testid={`del-svc-${s.id.slice(0, 6)}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><BedDouble className="w-4 h-4 text-[#2A9D8F]" /> Boarding rooms</h3>
              <Button variant="outline" size="sm" onClick={() => setRoomDialog(true)} data-testid="add-room-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {rooms.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 text-sm border-b border-[#E2F5F5] pb-2" data-testid={`room-row-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div>
                    <div className="font-medium">
                      {r.name} <Badge variant="outline" className="text-[9px] ml-1 capitalize">{r.type}</Badge>
                      {!r.active && <Badge className="ml-1 bg-slate-100 text-slate-500 hover:bg-slate-100 text-[9px]">inactive</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">{peso(r.daily_rate)}/night · capacity {r.capacity}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-7" title="Edit rate, type, capacity" onClick={() => setRoomEdit({ ...r })} data-testid={`edit-room-${r.id.slice(0, 6)}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => toggleRoomActive(r)} data-testid={`toggle-room-${r.id.slice(0, 6)}`}>{r.active ? "Deactivate" : "Activate"}</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-red-500" title="Remove room" onClick={() => deleteRoom(r)} data-testid={`del-room-${r.id.slice(0, 6)}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="accounting" className="mt-4 space-y-4">
          <div className="bg-[#D9F7F8] border border-[#0E7C84]/20 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <p className="font-semibold text-[#0E7C84]">Accounting integration: {settings?.accounting_provider || "Xero (MOCKED)"}</p>
              <p className="text-slate-600">Journal entries are auto-posted per sale. Sync is MOCKED for now — use CSV export for your accountant.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-journals-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
              <Button size="sm" className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={syncAccounting} data-testid="sync-accounting-btn"><RefreshCw className="w-4 h-4 mr-1" /> Sync now</Button>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#BFE8E9] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Date</TableHead><TableHead>Receipt</TableHead><TableHead>Journal lines</TableHead><TableHead>Sync</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {journals.map((j) => (
                  <TableRow key={j.id} data-testid={`journal-${j.receipt_no}`}>
                    <TableCell className="text-xs">{j.date?.slice(0, 10)}</TableCell>
                    <TableCell className="font-mono text-xs font-semibold">{j.receipt_no}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {j.lines.map((l, i) => (
                        <p key={i}>{l.account}: {l.debit ? `Dr ${peso(l.debit)}` : `Cr ${peso(l.credit)}`}</p>
                      ))}
                    </TableCell>
                    <TableCell>
                      {j.synced
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">{j.provider}</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Pending</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
                {journals.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-400 py-6">No journal entries yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <div className="bg-white rounded-2xl border border-[#BFE8E9] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>When</TableHead><TableHead>Channel</TableHead><TableHead>To</TableHead><TableHead>Content</TableHead><TableHead>Status</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((n) => (
                  <TableRow key={n.id} data-testid={`notif-${n.id.slice(0, 8)}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{n.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell><Badge variant="outline" className="uppercase text-[10px]">{n.type}</Badge></TableCell>
                    <TableCell className="text-xs">{n.to}</TableCell>
                    <TableCell className="text-xs max-w-[320px] truncate">{n.subject || n.message}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${n.status === "sent" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : n.status === "mocked" ? "bg-slate-100 text-slate-500 hover:bg-slate-100" : "bg-red-100 text-red-600 hover:bg-red-100"}`}>
                        {n.status === "mocked" ? "MOCKED SMS" : n.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {notifications.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-6">No notifications yet — they appear when bookings are made.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={userDialog} onOpenChange={setUserDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display">New staff account</DialogTitle><DialogDescription className="sr-only">Create a new staff account</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label>Password</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} data-testid="user-password" /></div>
            <div>
              <Label>Role</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {userForm.role === "groomer" && (
              <div><Label>Commission rate (0.35 = 35%)</Label><Input type="number" step="0.01" value={userForm.commission_rate} onChange={(e) => setUserForm({ ...userForm, commission_rate: e.target.value })} data-testid="user-commission" /></div>
            )}
          </div>
          <Button className="bg-[#0E7C84] hover:bg-[#093F46] mt-2" onClick={saveUser} disabled={!userForm.name || !userForm.email || userForm.password.length < 8} data-testid="user-save-btn">Create account</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit staff — {editUser?.email}</DialogTitle><DialogDescription className="sr-only">Edit staff account details</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={editUser?.name || ""} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name" /></div>
            <div>
              <Label>Role</Label>
              <Select value={editUser?.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Phone</Label><Input value={editUser?.phone || ""} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} data-testid="edit-user-phone" /></div>
            {editUser?.role === "groomer" && (
              <div><Label>Commission rate (0.35 = 35%)</Label><Input type="number" step="0.01" value={editUser?.commission_rate ?? 0} onChange={(e) => setEditUser({ ...editUser, commission_rate: e.target.value })} data-testid="edit-user-commission" /></div>
            )}
            <div><Label>New password (optional)</Label><Input type="password" value={editUser?.password || ""} onChange={(e) => setEditUser({ ...editUser, password: e.target.value })} placeholder="Leave blank to keep current" data-testid="edit-user-password" /></div>
          </div>
          <Button className="bg-[#0E7C84] hover:bg-[#093F46] mt-2" onClick={saveEditUser} disabled={!editUser?.name} data-testid="edit-user-save-btn">Save changes</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!svcEdit} onOpenChange={(v) => !v && setSvcEdit(null)}>
        <DialogContent data-testid="svc-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit service</DialogTitle><DialogDescription className="sr-only">Edit grooming service details</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={svcEdit?.name || ""} onChange={(e) => setSvcEdit({ ...svcEdit, name: e.target.value })} data-testid="svc-edit-name" /></div>
            <div>
              <Label>Tier</Label>
              <Select value={svcEdit?.tier || "Standard"} onValueChange={(v) => setSvcEdit({ ...svcEdit, tier: v })}>
                <SelectTrigger data-testid="svc-edit-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{["Express", "Standard", "Premium", "Specialty"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Base price (₱, small tier)</Label><Input type="number" value={svcEdit?.base_price ?? ""} onChange={(e) => setSvcEdit({ ...svcEdit, base_price: e.target.value })} data-testid="svc-edit-price" /></div>
            <div><Label>Duration (min)</Label><Input type="number" value={svcEdit?.duration_min ?? ""} onChange={(e) => setSvcEdit({ ...svcEdit, duration_min: e.target.value })} data-testid="svc-edit-duration" /></div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="svc-edit-active-label">
                <input type="checkbox" className="accent-[#0E7C84]" checked={svcEdit?.active ?? true} onChange={(e) => setSvcEdit({ ...svcEdit, active: e.target.checked })} data-testid="svc-edit-active" />
                Visible for booking
              </label>
            </div>
            <div className="col-span-2"><Label>Description</Label><Input value={svcEdit?.description || ""} onChange={(e) => setSvcEdit({ ...svcEdit, description: e.target.value })} data-testid="svc-edit-desc" /></div>
          </div>
          <Button className="bg-[#E84855] hover:bg-[#C62836] mt-2" onClick={saveSvcEdit} disabled={!svcEdit?.name} data-testid="svc-edit-save-btn">Save changes</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!roomEdit} onOpenChange={(v) => !v && setRoomEdit(null)}>
        <DialogContent data-testid="room-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit boarding room</DialogTitle><DialogDescription className="sr-only">Edit boarding room details</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={roomEdit?.name || ""} onChange={(e) => setRoomEdit({ ...roomEdit, name: e.target.value })} data-testid="room-edit-name" /></div>
            <div>
              <Label>Type</Label>
              <Select value={roomEdit?.type || "kennel"} onValueChange={(v) => setRoomEdit({ ...roomEdit, type: v })}>
                <SelectTrigger data-testid="room-edit-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["suite", "kennel", "condo"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Daily rate (₱)</Label><Input type="number" value={roomEdit?.daily_rate ?? ""} onChange={(e) => setRoomEdit({ ...roomEdit, daily_rate: e.target.value })} data-testid="room-edit-rate" /></div>
            <div><Label>Capacity</Label><Input type="number" value={roomEdit?.capacity ?? 1} onChange={(e) => setRoomEdit({ ...roomEdit, capacity: e.target.value })} data-testid="room-edit-capacity" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="accent-[#2A9D8F]" checked={roomEdit?.active ?? true} onChange={(e) => setRoomEdit({ ...roomEdit, active: e.target.checked })} data-testid="room-edit-active" />
              Available for booking
            </label>
          </div>
          <Button className="bg-[#2A9D8F] hover:bg-[#238b7e] mt-2" onClick={saveRoomEdit} disabled={!roomEdit?.name} data-testid="room-edit-save-btn">Save changes</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={svcDialog} onOpenChange={setSvcDialog}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-display">New grooming service</DialogTitle><DialogDescription className="sr-only">Add a new grooming service</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={svcForm.name} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })} data-testid="svc-name" /></div>
            <div>
              <Label>Tier</Label>
              <Select value={svcForm.tier} onValueChange={(v) => setSvcForm({ ...svcForm, tier: v })}>
                <SelectTrigger data-testid="svc-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{["Express", "Standard", "Premium", "Specialty"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Base price (₱, small tier)</Label><Input type="number" value={svcForm.base_price} onChange={(e) => setSvcForm({ ...svcForm, base_price: e.target.value })} data-testid="svc-price" /></div>
            <div><Label>Duration (min)</Label><Input type="number" value={svcForm.duration_min} onChange={(e) => setSvcForm({ ...svcForm, duration_min: e.target.value })} data-testid="svc-duration" /></div>
            <div><Label>Description</Label><Input value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} data-testid="svc-desc" /></div>
          </div>
          <Button className="bg-[#E84855] hover:bg-[#C62836] mt-2" onClick={saveService} disabled={!svcForm.name || !svcForm.base_price} data-testid="svc-save-btn">Add service</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={roomDialog} onOpenChange={setRoomDialog}>
        <DialogContent data-testid="room-dialog">
          <DialogHeader><DialogTitle className="font-display">New boarding room</DialogTitle><DialogDescription className="sr-only">Add a new boarding room</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={roomForm.name} onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })} data-testid="room-name" /></div>
            <div>
              <Label>Type</Label>
              <Select value={roomForm.type} onValueChange={(v) => setRoomForm({ ...roomForm, type: v })}>
                <SelectTrigger data-testid="room-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["suite", "kennel", "condo"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Daily rate (₱)</Label><Input type="number" value={roomForm.daily_rate} onChange={(e) => setRoomForm({ ...roomForm, daily_rate: e.target.value })} data-testid="room-rate" /></div>
            <div><Label>Capacity</Label><Input type="number" value={roomForm.capacity} onChange={(e) => setRoomForm({ ...roomForm, capacity: e.target.value })} data-testid="room-capacity" /></div>
          </div>
          <Button className="bg-[#2A9D8F] hover:bg-[#238b7e] mt-2" onClick={saveRoom} disabled={!roomForm.name || !roomForm.daily_rate} data-testid="room-save-btn">Add room</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
